import { motion } from 'framer-motion';
import {
  Building,
  ArrowRight,
  Check,
  Loader2,
  Target,
  Users,
  Globe,
  Lightbulb,
  CheckCircle2,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { companiesApi, CreateCompanyData } from '../services/api';
import { CLOUDFLARE_STATIC_ASSETS, getPageHeroImageUrl } from '../config/cloudflareAssets';
import PageHero from '../components/PageHero';

export default function CompanySetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateCompanyData>({
    name: '',
    size: '',
    industry: '',
    description: '',
  });

  const handleNext = async () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      // Submit form and create company
      await handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await companiesApi.create(formData);

      if (response.success && response.data) {
        // Success! Navigate to dashboard or company page
        navigate('/dashboard');
      } else {
        setError(response.error || 'Failed to create company');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const stepConfig = [
    {
      id: 1,
      title: 'Company Basics',
      subtitle: 'Tell us about your organization',
      icon: Building,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      id: 2,
      title: 'Industry & Focus',
      subtitle: 'Define your business context',
      icon: Globe,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      id: 3,
      title: 'Review & Launch',
      subtitle: 'Confirm and create your workspace',
      icon: Target,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src={CLOUDFLARE_STATIC_ASSETS.LOGO_LIGHT} alt="StratPlan" className="h-16" />
          </div>
          <div className="text-sm text-gray-600">Step {step} of 3</div>
        </div>
      </header>

      <PageHero
        heroImageUrl={heroImageUrl}
        overlayClassName="bg-gradient-to-br from-blue-900/50 via-purple-900/45 to-slate-900/50"
        title="Create Your Strategic Planning Workspace"
        subtitle="Set up your company profile and begin your strategic planning journey with our comprehensive framework"
      />

      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12">
            {stepConfig.map((stepItem, index) => (
              <div key={stepItem.id} className="flex items-center">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center text-sm font-semibold transition-all ${
                    stepItem.id <= step
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'bg-white text-gray-400 border-2 border-gray-200'
                  }`}
                >
                  {stepItem.id < step ? (
                    <Check className="h-5 w-5" />
                  ) : stepItem.id === 1 ? (
                    <Building className="h-5 w-5" />
                  ) : stepItem.id === 2 ? (
                    <Globe className="h-5 w-5" />
                  ) : (
                    <Target className="h-5 w-5" />
                  )}
                </div>
                {index < stepConfig.length - 1 && (
                  <div
                    className={`w-16 h-1 mx-4 rounded-full transition-all ${
                      stepItem.id < step
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600'
                        : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Current Step Info */}
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div
              className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-4 ${stepConfig[step - 1].bgColor} ${stepConfig[step - 1].color}`}
            >
              {step === 1 && <Building className="h-4 w-4 mr-2" />}
              {step === 2 && <Globe className="h-4 w-4 mr-2" />}
              {step === 3 && <Target className="h-4 w-4 mr-2" />}
              STEP {step}
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{stepConfig[step - 1].title}</h2>
            <p className="text-gray-600">{stepConfig[step - 1].subtitle}</p>
          </motion.div>

          {/* Form Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 max-w-2xl mx-auto"
          >
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                    placeholder="Enter your company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Company Size
                  </label>
                  <select
                    value={formData.size}
                    onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  >
                    <option value="">Select your company size</option>
                    <option value="startup">Startup (1-10 employees)</option>
                    <option value="small">Small (11-50 employees)</option>
                    <option value="medium">Medium (51-200 employees)</option>
                    <option value="large">Large (201-1000 employees)</option>
                    <option value="enterprise">Enterprise (1000+ employees)</option>
                  </select>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <Lightbulb className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">
                        Why we ask for company size
                      </h4>
                      <p className="text-sm text-blue-800">
                        This helps us customize your strategic planning experience with frameworks
                        and templates suited to your organization's scale and complexity.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">Industry</label>
                  <select
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  >
                    <option value="">Select your industry</option>
                    <option value="technology">Technology & Software</option>
                    <option value="healthcare">Healthcare & Life Sciences</option>
                    <option value="finance">Finance & Banking</option>
                    <option value="retail">Retail & E-commerce</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="consulting">Consulting & Professional Services</option>
                    <option value="education">Education</option>
                    <option value="nonprofit">Non-profit</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Company Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                    placeholder="Brief description of your company and what you do..."
                  />
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <Globe className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-purple-900 mb-1">
                        Industry-specific insights
                      </h4>
                      <p className="text-sm text-purple-800">
                        We'll provide relevant strategic frameworks, competitor analysis templates,
                        and industry benchmarks tailored to your sector.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Company Profile Summary
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-2 border-b border-gray-200">
                      <span className="text-sm font-medium text-gray-600">Company Name:</span>
                      <span className="font-semibold text-gray-900">{formData.name}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-200">
                      <span className="text-sm font-medium text-gray-600">Size:</span>
                      <span className="font-semibold text-gray-900 capitalize">
                        {formData.size}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-200">
                      <span className="text-sm font-medium text-gray-600">Industry:</span>
                      <span className="font-semibold text-gray-900 capitalize">
                        {formData.industry}
                      </span>
                    </div>
                    {formData.description && (
                      <div className="py-2">
                        <span className="text-sm font-medium text-gray-600">Description:</span>
                        <p className="font-medium text-gray-900 mt-1">{formData.description}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-6 w-6 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-green-900 mb-2">🎉 Ready to Launch!</h4>
                      <p className="text-sm text-green-800 mb-3">
                        Your strategic planning workspace will be created now. Activate Pro anytime
                        to unlock advanced features like:
                      </p>
                      <ul className="text-sm text-green-800 space-y-1">
                        <li className="flex items-center">
                          <Check className="h-3 w-3 mr-2" />
                          AI-powered strategy generation
                        </li>
                        <li className="flex items-center">
                          <Check className="h-3 w-3 mr-2" />
                          Advanced analytics and reporting
                        </li>
                        <li className="flex items-center">
                          <Check className="h-3 w-3 mr-2" />
                          Unlimited team collaboration
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="mt-6">
                {error.toLowerCase().includes('plan') ||
                error.toLowerCase().includes('company(ies)') ? (
                  // Plan limit error - show upgrade CTA
                  <div className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="h-12 w-12 bg-purple-600 rounded-full flex items-center justify-center">
                          <Target className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          🚀 Unlock Unlimited Companies
                        </h3>
                        <p className="text-sm text-gray-700 mb-1">
                          <strong>{error}</strong>
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                          To create more companies, upgrade <strong>any existing company</strong> to
                          Pro or Enterprise. One Pro subscription unlocks unlimited company
                          creation, plus AI-powered insights and advanced analytics.
                        </p>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                          <button
                            onClick={() => navigate('/dashboard')}
                            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2.5 rounded-lg hover:opacity-90 transition-opacity font-semibold shadow-md flex items-center justify-center"
                          >
                            Go to Dashboard & Upgrade
                            <ArrowRight className="h-4 w-4 ml-2" />
                          </button>
                          <button
                            onClick={() => navigate('/pricing')}
                            className="px-6 py-2.5 border border-purple-300 rounded-lg text-purple-700 hover:bg-purple-50 transition-colors font-medium"
                          >
                            View Pricing Details
                          </button>
                        </div>
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-xs text-blue-800">
                            💡 <strong>How it works:</strong> Go to your Dashboard → Select any
                            company → Click Settings → Upgrade to Pro. This unlocks unlimited
                            company creation for your account!
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Regular error display
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={handleBack}
                disabled={step === 1 || isLoading}
                className="px-6 py-3 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleNext}
                disabled={(step === 1 && !formData.name) || isLoading}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-lg hover:opacity-90 transition-opacity flex items-center disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    {step === 3 ? 'Create Company' : 'Continue'}
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Preview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What You'll Get With Your New Workspace
            </h2>
            <p className="text-lg text-gray-600">
              Your complete strategic planning ecosystem, ready to use
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                <Target className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Strategic Framework</h3>
              <p className="text-gray-600 text-sm">
                Complete 5-step methodology with proven frameworks like SWOT, PESTEL, and Porter's
                Five Forces
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Team Collaboration</h3>
              <p className="text-gray-600 text-sm">
                Invite team members, assign roles, and collaborate on strategic initiatives in
                real-time
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center mb-4">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Execution Hub</h3>
              <p className="text-gray-600 text-sm">
                Transform strategies into actionable tasks and track progress with comprehensive
                dashboards
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
